import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Transition } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import ascendLogo from "../../assets/ACS-Images/Ascend-Logo.png";
import signupImage from "../../assets/ACS-Images/signup-image.png";
import Loader from "../../components/ACS-Dashboard/ACS-Blocks/Loader";
import GoogleLogo from "../../assets/ACS-Images/Google-Logo.png";
import MicrosoftLogo from "../../assets/ACS-Images/Microsoft-Logo.png";
import OktaLogo from "../../assets/ACS-Images/Okta-Logo.png";
import AppleLogo from "../../assets/ACS-Images/Apple-Logo.png";
import SSOLogo from "../../assets/ACS-Images/SSO-Logo.png";

const SignUpPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const [fadeOut, setFadeOut] = useState(false);

  // Moved validation function to top level
  const validateForm = () => {
    const errors = {};
    if (!name.trim()) errors.name = "Name is required";
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Invalid email format";
    }
  
    // Remove this misplaced timeout block
    // setTimeout(() => {
    //   setFadeOut(true);
    //   setTimeout(() => navigate("/onboarding"), 700);
    // }, 1500);
  
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!password) {
      errors.password = "Password is required";
    } else if (!passwordRegex.test(password)) {
      errors.password = "Password must be at least 8 characters with uppercase, lowercase, number, and special character";
    }
  
    if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
  
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm(); // Now correctly referenced

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => toast.error(error));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/user/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409 && errorData.message?.toLowerCase().includes("already exists")) {
          setEmailError("This email is already registered");
          toast.error("This email is already registered");
          setLoading(false);
          return;
        }
        throw new Error(errorData.message || "Registration failed");
      }

      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.message || "An error occurred during registration");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={`flex flex-col md:flex-row min-h-screen w-full bg-white transition-opacity duration-500 ${fadeOut ? "opacity-0" : "opacity-100"} overflow-x-hidden`}>
      {/* Left Side - Image */}
      <div
        className="hidden md:block w-full md:w-1/2 h-screen bg-cover bg-center bg-no-repeat fixed"
        style={{ backgroundImage: `url(${signupImage})` }}
      />

      {/* Right Side - Sign-up Form */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 xs:p-5 sm:p-6 ml-0 md:ml-[50%]">
        <div className="w-full max-w-[90vw] xs:max-w-[85vw] sm:max-w-sm">
          {/* Logo Section */}
          <div className="text-center space-y-2 xs:space-y-3 sm:space-y-4">
            <img 
              src={ascendLogo} 
              alt="Ascend Logo"
              className="w-32 xs:w-36 sm:w-40 mx-auto mb-2 xs:mb-3 transition-transform duration-300 hover:scale-105"
            />
            <h1 className="text-xl xs:text-2xl sm:text-2xl font-black leading-tight">
              Reaching New Heights
            </h1>
            <p className="text-xs xs:text-sm sm:text-sm text-gray-600">
              ✅ Free trial for 15 days &nbsp; ✅ No credit card required
            </p>
          </div>

          {loading ? (
            <Loader message="Signing Up..." />
          ) : (
            <>
              <form className="w-full mt-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Name Input */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Name
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full p-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  {/* Password Input */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="w-full p-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Input */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="w-full p-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={agreedToTerms}
                      onChange={() => setAgreedToTerms(!agreedToTerms)}
                    />
                    <span className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-800">
                        terms and conditions
                      </a>
                    </span>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full p-2.5 text-sm bg-[#223688] text-white rounded-lg hover:bg-[#1c2c6d] transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-2 bg-white text-gray-500 text-sm">or</span>
                </div>
              </div>
            </>
          )}

          {/* SSO Login Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button className="flex items-center justify-center p-2 text-xs border rounded-lg hover:bg-gray-50">
              <img src={AppleLogo} alt="Apple" className="w-5 h-5 mr-2" />
              Sign up with Apple
            </button>
            <button className="flex items-center justify-center p-2 text-xs border rounded-lg hover:bg-gray-50">
              <img src={GoogleLogo} alt="Google" className="w-5 h-5 mr-2" />
              Sign up with Google
            </button>
            <button className="flex items-center justify-center p-2 text-xs border rounded-lg hover:bg-gray-50">
              <img src={MicrosoftLogo} alt="Microsoft" className="w-5 h-5 mr-2" />
              Sign up with Microsoft
            </button>
            <button className="flex items-center justify-center p-2 text-xs border rounded-lg hover:bg-gray-50">
              <img src={SSOLogo} alt="SSO" className="w-5 h-5 mr-2" />
              Sign up with SSO
            </button>
          </div>

          {/* Sign In Link */}
          {!loading && (
            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{" "}
              <button
                onClick={() => {
                  setFadeOut(true);
                  setTimeout(() => navigate("/login"), 500);
                }}
                className="text-blue-600 hover:text-blue-800 font-semibold"
              >
                Sign in
              </button>
            </p>
          )}
        

        </div>
      </div>

      <ToastContainer position="top-right" autoClose={5000} />

    
      {/* Success Modal */}
<Transition
  show={showSuccessModal}
  enter="ease-out duration-250"
  enterFrom="opacity-0 scale-95"
  enterTo="opacity-100 scale-100"
  leave="ease-in duration-200"
  leaveFrom="opacity-100 scale-100"
  leaveTo="opacity-0 scale-95"
>
  <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50">
    <div className="flex min-h-full items-center justify-center p-4 text-center">
      <Transition.Child
        enter="ease-out duration-250"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full mx-auto max-w-[90vw] sm:max-w-md min-w-[300px]">
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Registration Successful!
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              Your account has been created successfully.
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                setTimeout(() => navigate('/login'), 700);
              }}
              className="w-full bg-[#223688] text-white py-2 sm:py-3 text-sm sm:text-base rounded-md hover:opacity-90 transition-opacity duration-200"
            >
              Continue to Login
            </button>
          </div>
        </div>
      </Transition.Child>
    </div>
  </div>
</Transition>
    </div>
  );
};

export default SignUpPage;
